import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, Mail } from "lucide-react";
import { LOGO } from "@/lib/images";
import { track } from "@/lib/analytics";
import { setUiFlag } from "@/lib/uiState";

const links = [
  { label: "Home", to: "/" },
  { label: "Group Travel", to: "/group-travel" },
  { label: "Thailand 2027", to: "/Thailand2027", featured: true, testid: "nav-thailand-2027" },
  { label: "Cruises", to: "/cruises" },
  { label: "Resorts & Escapes", to: "/resorts" },
  { label: "Honeymoons", to: "/honeymoons" },
  { label: "Destinations", to: "/destinations" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

const ease = [0.76, 0, 0.24, 1];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Route change (menu tap, link, Back/Forward): always close the menu and release
  // the body scroll-lock immediately — never leave a frozen overlay behind.
  useEffect(() => { setOpen(false); document.body.style.overflow = ""; }, [location.pathname]);
  useEffect(() => { document.body.style.overflow = open ? "hidden" : ""; setUiFlag("menu", open); return () => { document.body.style.overflow = ""; }; }, [open]);

  // Same-route menu tap (e.g. "Home" while already on Home): no location change fires,
  // so close + unlock + reset scroll here. Cross-route taps are handled by the
  // pathname effect above + the scroll-restoration effect in SmoothScroll.
  const handleMenuNav = (to) => {
    setOpen(false);
    document.body.style.overflow = "";
    if (to === location.pathname) {
      requestAnimationFrame(() => {
        window.scrollTo(0, 0);
        if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
      });
    }
  };

  return (
    <>
      <header data-testid="main-navbar"
        className={`fixed top-0 left-0 right-0 z-[60] transition-colors duration-500 ${scrolled && !open ? "bg-black/80 backdrop-blur-xl border-b border-white/10" : "bg-transparent"}`}>
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 h-24 flex items-center justify-between">
          <Link to="/" data-testid="logo-link" className="flex items-center shrink-0" onClick={() => { window.scrollTo(0, 0); if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true }); }}>
            <img src={LOGO} alt="Inforno Travel" className="h-14 md:h-16 w-auto" />
            <span className="ml-3 hidden sm:inline font-body text-[11px] tracking-[0.4em] text-gold uppercase self-center">Travel</span>
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/start-your-journey" data-testid="nav-quote-cta"
              className="hidden md:inline-flex items-center border border-gold text-gold px-6 py-2.5 font-body text-[11px] uppercase tracking-[0.18em] hover:bg-gold hover:text-black transition-colors duration-300">
              Start Your Journey
            </Link>
            <button data-testid="menu-toggle" onClick={() => setOpen(true)} aria-label="Open menu"
              className="flex items-center gap-3 text-ink hover:text-gold transition-colors">
              <span className="hidden sm:inline font-body text-[11px] uppercase tracking-[0.2em]">Menu</span>
              <Menu size={26} strokeWidth={1.4} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div data-testid="fullscreen-menu"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[70] bg-noir grain">
            <div className="absolute top-0 left-0 right-0 h-24 max-w-[1500px] mx-auto px-6 md:px-12 flex items-center justify-between">
              <img src={LOGO} alt="Inforno Travel" className="h-14 md:h-16 w-auto" />
              <button data-testid="menu-close" onClick={() => setOpen(false)} aria-label="Close menu"
                className="flex items-center gap-3 text-ink hover:text-gold transition-colors">
                <span className="hidden sm:inline font-body text-[11px] uppercase tracking-[0.2em]">Close</span>
                <X size={26} strokeWidth={1.4} />
              </button>
            </div>

            <div className="h-full max-w-[1500px] mx-auto px-6 md:px-12 flex flex-col justify-start md:justify-center pt-28 pb-10 overflow-y-auto">
              <nav className="flex flex-col">
                {links.map((l, i) => (
                  <motion.div key={l.to}
                    initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
                    transition={{ duration: 0.6, delay: 0.08 + i * 0.05, ease }}>
                    <Link to={l.to} onClick={() => handleMenuNav(l.to)} data-testid={l.testid || `nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}
                      className="group flex items-baseline gap-4 py-2.5 w-fit">
                      <span className="font-body text-[10px] text-gold/60 tracking-widest w-6">{String(i + 1).padStart(2, "0")}</span>
                      <span className={`font-serif text-3xl sm:text-4xl md:text-6xl font-light transition-all duration-300 group-hover:text-gold group-hover:italic ${l.featured ? "text-gold/90" : "text-white/80"}`}>{l.label}</span>
                      {l.featured && (
                        <span className="self-start mt-1 font-body text-[9px] uppercase tracking-[0.25em] text-gold/90 border border-gold/40 rounded-full px-2 py-0.5">Featured</span>
                      )}
                    </Link>
                  </motion.div>
                ))}
              </nav>

              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
                className="mt-10 flex flex-col md:flex-row md:items-center gap-6 md:gap-12 border-t border-white/10 pt-8">
                <Link to="/start-your-journey" onClick={() => handleMenuNav("/start-your-journey")} className="inline-flex items-center justify-center border border-gold bg-gold text-black px-8 py-4 font-body text-xs uppercase tracking-[0.22em] hover:bg-transparent hover:text-gold transition-colors duration-300 w-fit">
                  Start Your Journey
                </Link>
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-8 text-white/60 text-sm">
                  <a href="tel:+12028165560" onClick={() => track("contact_phone_click", { source_page: location.pathname })} className="flex items-center gap-2 hover:text-gold transition-colors"><Phone size={15} className="text-gold" /> (202) 816-5560</a>
                  <a href="mailto:info@infornotravel.com" onClick={() => track("contact_email_click", { source_page: location.pathname })} className="flex items-center gap-2 hover:text-gold transition-colors"><Mail size={15} className="text-gold" /> info@infornotravel.com</a>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
